import { Anchor, Group, Stack, Table, Text } from '@mantine/core'
import { Link, useNavigate } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import type { Booking } from '@/frontend/hooks/useBookings'
import { CustomerAvatar } from './CustomerAvatar'
import { DataTable } from './DataTable'
import { KindBadge } from './KindBadge'
import { StatusBadge } from './StatusBadge'

interface Props {
  items: Booking[]
  total: number
  isLoading?: boolean
  page: number
  pageSize: number
  onPageChange: (p: number) => void
  emptyLabel?: string
  highlightPending?: boolean
  highlightCheckedIn?: boolean
}

const CHANNEL_ID: Record<string, string> = {
  WALK_IN: 'Walk-in',
  PHONE: 'Telepon',
  EMAIL: 'Email',
  TEXT_MESSAGE: 'Pesan',
  RADIO: 'Radio',
  AGENT: 'Agen',
  OTHER: 'Lainnya',
}

export function BookingTable({
  items,
  total,
  isLoading,
  page,
  pageSize,
  onPageChange,
  emptyLabel,
  highlightPending = false,
  highlightCheckedIn = false,
}: Props) {
  const navigate = useNavigate()
  const { t } = useTranslation()

  return (
    <DataTable
      isEmpty={!isLoading && items.length === 0}
      emptyLabel={emptyLabel}
      page={page}
      pageSize={pageSize}
      total={total}
      onPageChange={onPageChange}
      columns={
        <>
          <Table.Th>{t('component.booking_table.col_customer')}</Table.Th>
          <Table.Th>{t('component.booking_table.col_boat')}</Table.Th>
          <Table.Th>{t('component.booking_table.col_period')}</Table.Th>
          <Table.Th>{t('component.booking_table.col_channel')}</Table.Th>
          <Table.Th>{t('component.booking_table.col_status')}</Table.Th>
          <Table.Th ta="right">{t('component.booking_table.col_code')}</Table.Th>
        </>
      }
    >
      {items.map((b) => {
        const customerName = b.customer ? `${b.customer.firstName} ${b.customer.lastName}`.trim() : '—'
        const isPending = b.status === 'PENDING'
        const isWaitlisted = b.status === 'WAITLISTED'
        const durationDays = dayjs(b.checkOutAt).diff(dayjs(b.checkInAt), 'day')

        const isCheckedIn = b.status === 'CHECKED_IN'
        const rowStyle =
          highlightCheckedIn && isCheckedIn
            ? { background: 'light-dark(var(--mantine-color-laut-0), rgba(14,124,123,0.08))' }
            : highlightPending && isPending
              ? { background: 'light-dark(var(--mantine-color-karang-0), rgba(224,120,86,0.06))' }
              : highlightPending && isWaitlisted
                ? { background: 'light-dark(var(--mantine-color-orange-0), rgba(200,120,40,0.06))' }
                : undefined

        return (
          <Table.Tr
            key={b.id}
            style={{ cursor: 'pointer', ...rowStyle }}
            onClick={() => navigate({ to: `/dashboard/reservations/${b.id}` as any })}
          >
            {/* Pelanggan */}
            <Table.Td>
              <Group gap="sm" wrap="nowrap">
                <CustomerAvatar name={customerName} email={b.customer?.email} size={34} />
                <Stack gap={1}>
                  <Text size="sm" fw={600} truncate style={{ maxWidth: 160 }}>
                    {customerName}
                  </Text>
                  {b.customer?.category && <KindBadge category={b.customer.category} />}
                </Stack>
              </Group>
            </Table.Td>

            {/* Kapal */}
            <Table.Td>
              <Text size="sm" fw={500}>
                {b.boat?.name ?? '—'}
              </Text>
              {b.boat?.type && (
                <Text size="xs" c="dimmed">
                  {b.boat.type
                    .replace(/_/g, ' ')
                    .toLowerCase()
                    .replace(/^\w/, (c) => c.toUpperCase())}
                </Text>
              )}
            </Table.Td>

            {/* Periode — check-in → check-out + duration */}
            <Table.Td>
              <Text size="sm">
                {dayjs(b.checkInAt).format('DD MMM')} → {dayjs(b.checkOutAt).format('DD MMM YYYY')}
              </Text>
              <Text size="xs" c="dimmed">
                {durationDays > 0 ? `${durationDays} ${t('common.days')}` : `< 1 ${t('common.days')}`}
                {' · '}
                {t('component.booking_table.created_prefix')} {dayjs(b.createdAt).format('DD MMM')}
              </Text>
            </Table.Td>

            {/* Saluran */}
            <Table.Td>
              <Text size="sm">{b.channelLocation ?? 'Marina Office'}</Text>
              <Text size="xs" c="dimmed">
                {CHANNEL_ID[b.channel] ?? b.channel}
              </Text>
            </Table.Td>

            {/* Status */}
            <Table.Td>
              <StatusBadge status={b.status} kind="booking" upper />
            </Table.Td>

            {/* Booking link */}
            <Table.Td ta="right" onClick={(e) => e.stopPropagation()}>
              <Anchor component={Link} to={`/dashboard/reservations/${b.id}` as any} c="laut" size="sm" fw={500}>
                {b.code} →
              </Anchor>
            </Table.Td>
          </Table.Tr>
        )
      })}
    </DataTable>
  )
}
